import { Button, Popover, Segmented, Tag, Tooltip } from "antd";
import InlineEditTitle from "components/inline-edit-title";
import { CiqIconBox, CopyIcon } from "components/svg-icons";
import { useEffect, useState } from "react";
import "./bid-package-header.scss";
import ErrorBoundary from "components/error-boundary";
import { useHistory, useLocation, useParams } from "react-router";
import { ArrowLeftOutlined } from "@ant-design/icons";
import FeatureLogNavigator from "components/feature-log-navigator";
import type { FeatureLogNavReaderResult } from "hooks/use-feature-log-nav-reader";
import FeatureDuplicate from "components/feature-duplicate";
import { FeatureTypes } from "entity-app/constants";

/**
 * Handles tab navigation for the segmented control. Extracted so the navigation
 * logic can be unit-tested independently of the (currently single-option,
 * therefore not rendered) Segmented control. Behaviour is identical to the
 * previous inline handler.
 */
export const handleTabChange = ({
  value,
  setTabIndex,
  history,
  projectId,
  designPackageId
}: {
  value: string;
  setTabIndex: (value: string) => void;
  history: { push: (path: string) => void };
  projectId: string;
  designPackageId: string;
}) => {
  setTabIndex(value);
  history.push(
    `/project/${projectId}/design-packages/${designPackageId}?tab=${value}`
  );
};

function FeatureHeader(props: {
  title: string;
  subTitle?: string;
  actions?: any;
  headerTitle?: string;
  number?: string;
  status?: any;
  disabled?: boolean;
  feature: "BID_PACKAGE" | "DESIGN_PACKAGE";
  featureInstanceData?: any;
  goBackToList: () => void;
  navigator?: FeatureLogNavReaderResult;
}) {
  const {
    actions,
    title,
    subTitle,
    headerTitle,
    number,
    status,
    disabled,
    feature,
    featureInstanceData,
    goBackToList,
    navigator
  } = props;
  const { projectId, designPackageId } = useParams() as any;
  const location = useLocation();
  const history = useHistory();
  const tab = new URLSearchParams(location.search).get("tab");
  const [tabIndex, setTabIndex] = useState<string>(tab || "1");

  const [visible, setVisible] = useState(false);
  const [headerInfo, setHeaderInfo] = useState<{
    featureHeaderNumber?: string;
    featureHeaderTitle?: string;
  }>();

  const updateHeaderInfo = async (headersInfo: any) => {
    setHeaderInfo({
      featureHeaderNumber: headersInfo?.number,
      featureHeaderTitle: headersInfo?.title
    });
    await actions.updateFeatureInstanceFields({
      title: headersInfo?.title,
      number: headersInfo?.number
    });
  };

  useEffect(() => {
    setHeaderInfo({
      featureHeaderNumber: number,
      featureHeaderTitle: headerTitle
    });
  }, [headerTitle, number]);

  const segmentOptions = [{ label: "Details", value: "1" }];

  // Duplicating a design package does not carry over its custom milestones.
  const canDuplicate = feature !== "DESIGN_PACKAGE";

  return (
    <div className="flex flex-col w-full bg-white px-6 py-4 space-y-2">
      <div className="flex justify-between items-center">
        <div className="flex space-x-2 items-center">
          <Button
            icon={<ArrowLeftOutlined />}
            className="text-one text-black !bg-transparent !border-0 px-0 !shadow-none"
            onClick={goBackToList}
            data-testid="index-button-back-to-list"
          />
          <CiqIconBox feature={feature} size={20} padding={8} />
          <div className="flex-col pl-1">
            <div className="flex items-center space-x-2">
              <div className="text-xl font-semibold">{title}</div>
              <Tag>Beta</Tag>
            </div>
            {subTitle && <div className="text-xs font-normal">{subTitle}</div>}
          </div>
          {/* segmentOptions is a fixed single-element literal, so length > 1 is always false; the Segmented control (and its onChange) never renders */}
          {/* v8 ignore start */}
          {segmentOptions.length > 1 && (
            <Segmented
              shape="round"
              size="large"
              value={tabIndex}
              options={segmentOptions}
              onChange={(value) =>
                handleTabChange({
                  value: value as string,
                  setTabIndex,
                  history,
                  projectId,
                  designPackageId
                })
              }
            />
          )}
          {/* v8 ignore stop */}
          {navigator?.visible && (
            <FeatureLogNavigator
              visible={navigator.visible}
              currentIndex={navigator.currentIndex}
              total={navigator.total}
              onPrev={navigator.goPrev}
              onNext={navigator.goNext}
            />
          )}
        </div>

        {canDuplicate && (
          <div className="flex space-x-2 items-center">
            <Popover
              open={!disabled && visible}
              placement="bottomRight"
              content={
                <ErrorBoundary>
                  <FeatureDuplicate
                    featureInstanceData={featureInstanceData}
                    actions={actions}
                    currentFeatureType={FeatureTypes.BID_PACKAGE}
                    setVisible={setVisible}
                    visible={visible}
                  />
                </ErrorBoundary>
              }
              trigger="click"
              onOpenChange={() => {
                if (!disabled) setVisible(!visible);
              }}
              destroyOnHidden
            >
              <Tooltip>
                <Button
                  onClick={() => setVisible(true)}
                  disabled={disabled}
                  data-testid="index-button-duplicate"
                >
                  <div className="flex items-center space-x-2">
                    <div className=" text-[12px]">Duplicate</div>
                    <CopyIcon />
                  </div>
                </Button>
              </Tooltip>
            </Popover>
          </div>
        )}
      </div>

      <div className="flex py-2 md:px-16 lg:px-24 sm:px-4 items-center justify-center">
        <div className="flex w-full p-4 bg-ciq-gray-50 bg-opacity-60 items-center justify-between">
          <ErrorBoundary>
            <InlineEditTitle
              isPermissionToEdit={!disabled}
              headerInfo={headerInfo}
              updateHeaderInfo={updateHeaderInfo}
              feature={feature}
            />
          </ErrorBoundary>
          <div className="flex items-center">{status}</div>
        </div>
      </div>
    </div>
  );
}

export default FeatureHeader;
